import { useState, type ChangeEvent } from "react";
import dynamic from "next/dynamic";
import { useForm } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { Temporal } from "temporal-polyfill";

import {
  Text,
  Checkbox,
  Box,
  DateField,
  TextField,
  ErrorText,
  Loader,
} from "@cruk/cruk-react-components";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { calcLength } from "@fwa/src/utils/formUtils";
import {
  ACTIVITY_IN_MEMORY_MAX_LENGTH,
  fundraisingPageActivityInMemoryValidation,
  type InMemoryFormValues,
} from "@fwa/src/validation/page";

import { Editable } from "@fwa/src/components/Editable";
import { ErrorBoundary } from "@fwa/src/components/ErrorBoundary";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/EditableInMemoryForm/styles.module.css";

import {
  type FundraisingPageType,
  type InMemoryDateType,
  type PageType,
} from "@fwa/src/types";

const InMemory = dynamic(() => import("@fwa/src/components/InMemory"), {
  ssr: false,
  loading: () => <Loader />,
});

const validatedPlainDate = (isoDateString: string) => {
  let plainDate: Temporal.PlainDate | null;
  try {
    plainDate = Temporal.PlainDate.from(isoDateString);
  } catch {
    plainDate = null;
  }
  return plainDate;
};

const getDatesFromState = (newState: InMemoryDateType) => {
  let birthDatePlainDateTemporal: Temporal.PlainDate | null;
  let deathDatePlainDateTemporal: Temporal.PlainDate | null;
  try {
    birthDatePlainDateTemporal = Temporal.PlainDate.from(
      {
        year:
          newState.birthYear && parseInt(newState.birthYear, 10) > 111
            ? parseInt(newState.birthYear, 10)
            : undefined,
        month: newState.birthMonth
          ? parseInt(newState.birthMonth, 10)
          : undefined,
        day: newState.birthDay ? parseInt(newState.birthDay, 10) : undefined,
      },
      { overflow: "reject" },
    );
  } catch {
    birthDatePlainDateTemporal = null;
  }
  try {
    deathDatePlainDateTemporal = Temporal.PlainDate.from(
      {
        year:
          newState.deathYear && parseInt(newState.deathYear, 10) > 111
            ? parseInt(newState.deathYear, 10)
            : undefined,
        month: newState.deathMonth
          ? parseInt(newState.deathMonth, 10)
          : undefined,
        day: newState.deathDay ? parseInt(newState.deathDay, 10) : undefined,
      },
      { overflow: "reject" },
    );
  } catch {
    deathDatePlainDateTemporal = null;
  }

  const birthDate: Date | null = birthDatePlainDateTemporal
    ? new Date(
        birthDatePlainDateTemporal.year,
        birthDatePlainDateTemporal.month - 1,
        birthDatePlainDateTemporal.day,
        12,
      )
    : null;

  const deathDate: Date | null = deathDatePlainDateTemporal
    ? new Date(
        deathDatePlainDateTemporal.year,
        deathDatePlainDateTemporal.month - 1,
        deathDatePlainDateTemporal.day,
        12,
      )
    : null;

  return {
    birthDate,
    deathDate,
    birthIsoString: birthDatePlainDateTemporal
      ? birthDatePlainDateTemporal.toString()
      : null,
    deathIsoString: deathDatePlainDateTemporal
      ? deathDatePlainDateTemporal.toString()
      : null,
  };
};

// We need to submit dates as yyyy-mm-dd
// so we need to take the days the months and the years figure out if they make valid dates
// and then convert them to the ISO string format
const inMemoryStateDateToIsoDates = (newState: InMemoryDateType) => {
  const { birthIsoString, deathIsoString } = getDatesFromState(newState);
  return {
    birthISOString: birthIsoString || null,
    deathISOString: deathIsoString || null,
  };
};

export const EditableInMemoryForm = ({
  page,
  handleEditData,
  canEdit,
}: {
  page: FundraisingPageType;
  name?: string;
  handleEditData: (
    data: Partial<PageType>,
    refresh?: boolean,
  ) => Promise<void | PageType>;
  canEdit: boolean;
}) => {
  const { trackError } = useTracking();

  const { dateOfBirthDeceased, dateOfDeathDeceased, activityInMemory } = page;
  const birthDateParsed = validatedPlainDate(dateOfBirthDeceased || "");
  const deathDateParsed = validatedPlainDate(dateOfDeathDeceased || "");

  const [showDate, setShowDate] = useState<boolean>(
    !!dateOfBirthDeceased || !!dateOfDeathDeceased || false,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [nameCharCount, setNameCharCount] = useState(
    calcLength(activityInMemory || ""),
  );

  const [dateState, setDateState] = useState<InMemoryDateType>({
    birthDay: birthDateParsed?.day?.toString(10) ?? null,
    birthMonth: birthDateParsed?.month?.toString(10) ?? null,
    birthYear: birthDateParsed?.year?.toString(10) ?? null,
    deathDay: deathDateParsed?.day?.toString(10) ?? null,
    deathMonth: deathDateParsed?.month?.toString(10) ?? null,
    deathYear: deathDateParsed?.year?.toString(10) ?? null,
  });

  const defaultValues = {
    activityInMemory: page.activityInMemory || "",
    dateOfBirthDeceased: birthDateParsed
      ? new Date(birthDateParsed.toString())
      : undefined,
    dateOfDeathDeceased: deathDateParsed
      ? new Date(deathDateParsed.toString())
      : undefined,
  };

  const formProps = useForm<InMemoryFormValues>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues,
    criteriaMode: "firstError",
    shouldFocusError: true,
    resolver: standardSchemaResolver(fundraisingPageActivityInMemoryValidation),
    resetOptions: {
      keepDirtyValues: true, // user-interacted input will be retained
      keepErrors: false, // input errors will be retained with value update
    },
  });

  const {
    clearErrors,
    register,
    trigger,
    setValue,
    setError,
    formState: { errors },
    getValues,
  } = formProps;

  const doSubmit = async (): Promise<PageType | void> => {
    clearErrors();
    if (showDate) {
      const isFormValid = await trigger();
      if (!isFormValid) {
        setIsLoading(false);
        return;
      }
    } else {
      const isNameValid = await trigger("activityInMemory");
      if (!isNameValid) {
        setIsLoading(false);
        return;
      }
    }
    // once validation passes
    setIsLoading(true);
    const { birthISOString, deathISOString } =
      inMemoryStateDateToIsoDates(dateState);
    // convert dates to ISOString for FWS
    const dateOfBirthDeceased = showDate ? birthISOString : null;
    const dateOfDeathDeceased = showDate ? deathISOString : null;

    return handleEditData({
      activityInMemory: getValues("activityInMemory"),
      dateOfBirthDeceased,
      dateOfDeathDeceased,
    })
      .then((pageData) => {
        setIsLoading(false);
        return pageData;
      })
      .catch((error) => {
        setError("root", { type: "submit", message: "Unable to submit" });
        trackError(error as Error, { component: "EditableInMemoryForm" });
        throw error;
      });
  };

  // hides/shows the dates
  const handleShowDatesChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShowDate(e.target.checked);
  };

  // we don't want to show the error message
  // when people are still hopping from date to month to year
  const handleBirthDateFocus = () => {
    clearErrors("dateOfBirthDeceased");
  };
  const handleDeathDateFocus = () => {
    clearErrors("dateOfDeathDeceased");
  };
  const handleNameBlur = () => {
    trigger("activityInMemory", { shouldFocus: true }).catch(() => {
      // no op don't let revalidation err break form
    });
  };
  const handleBirthDateBlur = () => {
    if (
      dateState.birthDay?.length &&
      dateState.birthMonth?.length &&
      dateState.birthYear?.length
    ) {
      trigger("dateOfBirthDeceased").catch(() => {
        // no op don't let revalidation err break form
      });
    }
  };
  const handleDeathDateBlur = () => {
    if (
      dateState.deathDay?.length &&
      dateState.deathMonth?.length &&
      dateState.deathYear?.length
    ) {
      trigger("dateOfDeathDeceased").catch(() => {
        // no op don't let revalidation err break form
      });
    }
  };

  // updates date state when there is a change in any date component

  const handleDateChanged = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const newState = { ...dateState, [`${input.name}`]: input.value };

    // Controlled val of DateField
    setDateState(newState);

    // these are used for form validation
    setValue(
      "dateOfBirthDeceasedPartial",
      `${newState.birthDay || ""}${newState.birthMonth || ""}${
        newState.birthYear || ""
      }`,
    );
    setValue(
      "dateOfDeathDeceasedPartial",
      `${newState.deathDay || ""}${newState.deathMonth || ""}${
        newState.deathYear || ""
      }`,
    );

    // if temporal gives us string then the date must be valid without an overflow error
    const { birthDate, deathDate } = getDatesFromState(newState);
    setValue("dateOfBirthDeceased", birthDate ?? undefined);
    setValue("dateOfDeathDeceased", deathDate ?? undefined);
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNameCharCount(calcLength(e.target.value));
    setValue("activityInMemory", e.target.value);
  };

  return canEdit ? (
    <div data-component="page-in-memory-form">
      <Editable
        fieldName="inMemory"
        editNode={
          <form noValidate data-component="edit-in-memory-form">
            <Text>In memory of</Text>
            <>
              <TextField
                {...register("activityInMemory")}
                aria-label="in memory name"
                label=""
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                errorMessage={errors.activityInMemory?.message}
                maxLength={ACTIVITY_IN_MEMORY_MAX_LENGTH}
              />
              <Text textAlign="right" marginTop="xxs">{`${
                ACTIVITY_IN_MEMORY_MAX_LENGTH - nameCharCount
              } characters remaining`}</Text>
            </>
            <Box marginBottom="s">
              <Checkbox onChange={handleShowDatesChange} checked={showDate}>
                Add memorial dates
              </Checkbox>
            </Box>
            {showDate && (
              <Box>
                <div className={sharedStyles["responsive-row"]}>
                  <div className={styles["date-field-wrapper"]}>
                    <DateField
                      dayName="birthDay"
                      monthName="birthMonth"
                      yearName="birthYear"
                      day={dateState.birthDay ?? ""}
                      month={dateState.birthMonth ?? ""}
                      year={dateState.birthYear ?? ""}
                      label="When were they born?"
                      onChange={handleDateChanged}
                      onBlur={handleBirthDateBlur}
                      onFocus={handleBirthDateFocus}
                      errorMessage={errors.dateOfBirthDeceased?.message}
                    />
                  </div>

                  <div className={styles["date-field-wrapper"]}>
                    <DateField
                      dayName="deathDay"
                      monthName="deathMonth"
                      yearName="deathYear"
                      day={dateState.deathDay ?? ""}
                      month={dateState.deathMonth ?? ""}
                      year={dateState.deathYear ?? ""}
                      label="When did they pass away?"
                      onChange={handleDateChanged}
                      onBlur={handleDeathDateBlur}
                      onFocus={handleDeathDateFocus}
                      errorMessage={errors.dateOfDeathDeceased?.message}
                    />
                  </div>
                </div>
              </Box>
            )}
            {errors?.root?.message ? (
              <ErrorText>{errors?.root?.message}</ErrorText>
            ) : null}
          </form>
        }
        viewNode={
          !activityInMemory?.length ? (
            // placeholder view when no in memory name set
            <div className={sharedStyles["dashed-border"]}>
              <Text>Are you raising money in memory of someone?</Text>
            </div>
          ) : (
            <ErrorBoundary>
              <InMemory
                name={getValues("activityInMemory") || ""}
                birthDateString={dateOfBirthDeceased || undefined}
                deathDateString={dateOfDeathDeceased || undefined}
              />
            </ErrorBoundary>
          )
        }
        handleSubmit={doSubmit}
        isLoading={isLoading}
        tooltip="Edit in memory details"
      />
    </div>
  ) : page.activityInMemory ? (
    <Box marginBottom="none">
      <ErrorBoundary>
        <InMemory
          name={page.activityInMemory}
          birthDateString={page.dateOfBirthDeceased}
          deathDateString={page.dateOfDeathDeceased}
        />
      </ErrorBoundary>
    </Box>
  ) : null;
};

export default EditableInMemoryForm;
